/**
 * The `mirror-image` decoy's look (AD-833; combat-depth.md §3.3a). The server sends the decoy as
 * a `player`-shaped entity wearing its caster's appearance, with `summonOf` naming the caster; the
 * client draws it with the ordinary remote-player rig and then makes it read as an IMAGE rather
 * than a second adventurer: every mesh material is cloned (the rig's materials can be shared with
 * other bodies) and made translucent with a cool emissive sheen.
 *
 * Pure THREE scene-graph work, no renderer state: callable on the placeholder capsule at entry and
 * again on the rig root when it settles. Idempotent — a material already marked is left alone.
 */

import * as THREE from 'three';

/** The image's opacity and sheen. Presentation only — no gameplay reads them. */
export const MIRROR_IMAGE_OPACITY = 0.55;
export const MIRROR_IMAGE_SHEEN = '#7fd8ff';

const MARK = 'gcMirrorImage';

function mirrorMaterial(m: THREE.Material): THREE.Material {
  if (m.userData[MARK] === true) return m;
  const c = m.clone();
  c.transparent = true;
  c.opacity = MIRROR_IMAGE_OPACITY;
  c.depthWrite = false;
  if (c instanceof THREE.MeshStandardMaterial) {
    c.emissive = new THREE.Color(MIRROR_IMAGE_SHEEN);
    c.emissiveIntensity = 0.45;
  }
  c.userData[MARK] = true;
  return c;
}

/** Give every mesh under `root` the mirror-image look. Returns how many meshes it touched. */
export function applyMirrorImageLook(root: THREE.Object3D): number {
  let touched = 0;
  root.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!(mesh as { isMesh?: boolean }).isMesh || mesh.material === undefined) return;
    mesh.material = Array.isArray(mesh.material)
      ? mesh.material.map(mirrorMaterial)
      : mirrorMaterial(mesh.material);
    mesh.castShadow = false; // an image casts no shadow
    touched += 1;
  });
  return touched;
}

/** Whether a wire entity is a summoned image (its `summonOf` names a caster). */
export function isSummonedImage(state: { summonOf?: number }): boolean {
  return typeof state.summonOf === 'number';
}
